import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faTimesCircle, faEdit, faTrash, faUsers, faCalendarAlt, faHardHat
} from '@fortawesome/free-solid-svg-icons';
import styles from '../../pages/Dashboard.module.css';

const TimesheetCardView = ({
    filteredEntries,
    selectedEntries,
    setSelectedEntries,
    toggleSelectAll,
    toggleSelectEntry,
    handleBulkApprove,
    handleBulkReject,
    handleSingleApprove,
    handleSingleReject,
    handleEdit,
    handleDelete,
    isWeekend,
    getHoursColor,
    getStatusBadge,
    searchTerm,
    dateFilter,
    employeeFilter,
    chantierFilter
}) => {
    return (
        <>
            {/* Bulk Actions Bar */}
            {selectedEntries.length > 0 && (
                <div style={{
                    background: 'var(--color-primary)',
                    color: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: 'var(--border-radius-lg)',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ fontWeight: 'bold' }}>
                        {selectedEntries.length} entrée(s) sélectionnée(s)
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleBulkApprove}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            Valider
                        </button>
                        <button
                            onClick={handleBulkReject}
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FontAwesomeIcon icon={faTimesCircle} />
                            Rejeter
                        </button>
                        <button
                            onClick={() => setSelectedEntries([])}
                            style={{
                                background: 'transparent',
                                color: 'white',
                                border: '1px solid white',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            {/* Global Select All Checkbox */}
            {filteredEntries.length > 0 && (
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="checkbox"
                        checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                        id="selectAllCards"
                    />
                    <label htmlFor="selectAllCards" style={{ cursor: 'pointer', fontWeight: 'bold', color: 'var(--color-text)' }}>
                        Tout sélectionner
                    </label>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                {filteredEntries.map((entry) => {
                    const statusBadge = getStatusBadge(entry.status);
                    const entryIsWeekend = isWeekend(entry.date);
                    const isSelected = selectedEntries.includes(entry.id);

                    return (
                        <div key={entry.id} style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--border-radius-lg)',
                            padding: '1.5rem',
                            position: 'relative',
                            boxShadow: isSelected ? '0 0 0 2px var(--color-primary)' : 'var(--shadow-md)',
                            border: isSelected ? 'none' : '1px solid var(--color-light-gray)',
                            transition: 'all 0.2s',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {/* Header: Date & Checkbox */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSelectEntry(entry.id)}
                                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                    />
                                    <span style={{ fontWeight: '700', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ color: 'var(--color-text-light)' }} />
                                        {new Date(entry.date).toLocaleDateString('fr-FR')}
                                    </span>
                                    {entryIsWeekend && (
                                        <span style={{
                                            fontSize: '0.7rem',
                                            padding: '2px 6px',
                                            background: '#3b82f6',
                                            color: 'white',
                                            borderRadius: '4px',
                                            fontWeight: 'bold'
                                        }}>
                                            W-E
                                        </span>
                                    )}
                                </div>
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '4px 10px',
                                    background: statusBadge.bg,
                                    color: statusBadge.color,
                                    borderRadius: '20px',
                                    fontSize: '0.75rem',
                                    fontWeight: '700'
                                }}>
                                    <FontAwesomeIcon icon={statusBadge.icon} />
                                    {statusBadge.label}
                                </span>
                            </div>

                            {/* Main Info */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FontAwesomeIcon icon={faUsers} style={{ color: 'var(--color-primary)' }} />
                                        <span style={{ fontWeight: '600' }}>{entry.workerName || entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu'}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FontAwesomeIcon icon={faHardHat} style={{ color: '#f59e0b' }} />
                                        <span style={{ fontWeight: '600', color: 'var(--color-text-light)' }}>{entry.expand?.chantier?.name || '-'}</span>
                                    </div>
                                </div>
                                <div style={{
                                    fontWeight: '800',
                                    fontSize: '1.5rem',
                                    color: getHoursColor(entry.hours),
                                    padding: '8px 12px',
                                    background: getHoursColor(entry.hours) + '15',
                                    borderRadius: '12px',
                                    textAlign: 'center'
                                }}>
                                    {entry.hours}h
                                </div>
                            </div>

                            {/* Description */}
                            <div style={{
                                color: 'var(--color-text-light)',
                                fontStyle: 'italic',
                                fontSize: '0.9rem',
                                background: 'var(--color-background)',
                                padding: '10px',
                                borderRadius: '8px',
                                minHeight: '40px'
                            }}>
                                {entry.description || 'Aucune description'}
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--color-light-gray)' }}>
                                {(entry.status === 'pending' || !entry.status || entry.status === 'pending_manager' || entry.status === 'pending_rh') && (
                                    <>
                                        <button
                                            className={styles.iconBtn}
                                            onClick={() => handleSingleApprove(entry.id)}
                                            title="Valider"
                                            style={{ background: '#10b981', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                                        >
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </button>
                                        <button
                                            className={styles.iconBtn}
                                            onClick={() => handleSingleReject(entry.id)}
                                            title="Rejeter"
                                            style={{ background: '#ef4444', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                                        >
                                            <FontAwesomeIcon icon={faTimesCircle} />
                                        </button>
                                    </>
                                )}
                                <button className={styles.edit} onClick={() => handleEdit(entry)} title="Modifier" style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', cursor: 'pointer' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button className={styles.delete} onClick={() => handleDelete(entry.id)} title="Supprimer" style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {filteredEntries.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', background: 'var(--color-surface)', borderRadius: 'var(--border-radius-lg)', border: '1px dashed var(--color-light-gray)' }}>
                        {searchTerm || dateFilter !== 'ALL' || employeeFilter !== 'ALL' || chantierFilter !== 'ALL'
                            ? 'Aucune entrée ne correspond à vos critères.'
                            : 'Aucune entrée d\'heure trouvée.'
                        }
                    </div>
                )}
            </div>
        </>
    );
};

export default TimesheetCardView;
